import { useCampaignsStore } from '@/stores/campaigns'
import { isAfter, isBefore } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'

import SeptemberNewBonus2026Terms from '@/components/campaigns/terms-and-conditions/2026/SeptemberNewBonus2026Terms.vue'
import SpringSale2026Terms from '@/components/campaigns/terms-and-conditions/2026/SpringSale2026Terms.vue'

// Do not remove group subs banner
const GroupSubsBanner = defineAsyncComponent(
	() => import('@/components/campaigns/banners/2026/GroupSubsBanner.vue')
)

const SeptemberNewBonus2026Banner = defineAsyncComponent(
	() =>
		import(
			'@/components/campaigns/banners/2026/SeptemberNewBonus2026Banner.vue'
		)
)

const SpringSale2026Banner = defineAsyncComponent(
	() => import('@/components/campaigns/banners/2026/SpringSale2026Banner.vue')
)

export const isCampaignActive = (startDate, endDate) => {
	// Define the AEDT time zone
	const timeZone = 'Australia/Sydney'

	// If startDate or endDate is not provided or is invalid, return false
	if (!startDate && !endDate) {
		return false
	}

	// Create the campaign date in AEDT UTC
	const campaignStartDateStr = startDate
	const campaignEndDateStr = endDate
	const campaignStartDateUTC = fromZonedTime(campaignStartDateStr, timeZone)
	const campaignEndDateUTC = fromZonedTime(campaignEndDateStr, timeZone)

	// Get the current date in UTC
	const nowUTC = new Date()

	// Check if the current date is between campaign start and end date
	return (
		isBefore(campaignStartDateUTC, nowUTC) &&
		isAfter(campaignEndDateUTC, nowUTC)
	)
}

export const hasValidDiscountSet = (discountSet) => {
	if (Array.isArray(discountSet)) return discountSet.length > 0

	if (typeof discountSet === 'object' && discountSet !== null)
		return Object.keys(discountSet).length > 0

	return !!discountSet
}

export const getCampaignBadgeInfo = (discountSet, customBadge) => {
	// Set default values in case tag doesn't match any case
	let badgeText = discountSet.badge
	let badgeColor = 'bg-blue-400'
	let badgeTextColor = 'text-white'

	// If a custom badge is provided, use that as the text
	if (customBadge) {
		badgeText = customBadge
	} else {
		// Determine text and badgeColor based on tag
		switch (discountSet.tag) {
			case 'new_bonus':
				badgeColor = 'bg-[#00FFF9]'
				badgeTextColor = 'text-gray-700'
				break
			case 'spring_sale_2026':
				badgeColor = 'bg-[#F95843]'
				badgeTextColor = 'text-white'
				break
			default:
				// Use the default values set above
				break
		}
	}

	return { badgeText, badgeColor, badgeTextColor }
}

// To show secondary campaign promotion
// ie. novated lease Tesla promo
export const showNovatedLeaseCampaign = (
	campaignKey = 'novated_lease_tesla_incentive'
) => {
	const campaigns = useCampaignsStore()
	const showCampaign = campaigns.showCampaignStatus(campaignKey)
	const isNovatedLeaseStickyActive = computed(() => showCampaign)

	return { isNovatedLeaseStickyActive }
}

const campaignKeys = ['new_subscriber_bonus_2026', 'spring_sale_2026']

export const setPrimaryCampaignKey = () => {
	const campaigns = useCampaignsStore()

	campaigns.primaryCampaignKey = null

	for (const key of campaignKeys) {
		const campaign = campaigns.campaigns[key]

		if (
			campaign &&
			isCampaignActive(campaign.start_date, campaign.end_date)
		) {
			campaigns.primaryCampaignKey = key
			break
		}
	}
}

export const setSecondaryCampaignKey = (preferredKey) => {
	const campaigns = useCampaignsStore()

	// Explicit override
	if (preferredKey) {
		const campaign = campaigns.campaigns[preferredKey]
		if (
			campaign &&
			isCampaignActive(campaign.start_date, campaign.end_date)
		) {
			campaigns.secondaryCampaignKey = preferredKey
			return
		}
	}

	// Find all active campaigns
	const activeKeys = campaignKeys.filter((key) => {
		const campaign = campaigns.campaigns[key]
		return (
			campaign && isCampaignActive(campaign.start_date, campaign.end_date)
		)
	})

	// Set the first active campaign as secondary campaign, if any
	campaigns.secondaryCampaignKey =
		activeKeys.length === 2 ? activeKeys[0] : null
}

export const showStickyCampaign = (preferredKey) => {
	const campaigns = useCampaignsStore()

	const isSecondaryCampaignActive = computed(() => {
		if (preferredKey) {
			return campaigns.getSecondaryCampaignKey === preferredKey
		}

		return campaigns.getSecondaryCampaignKey !== null
	})

	return { isSecondaryCampaignActive }
}

export const getPrimaryCampaignPromo = () => {
	const campaigns = useCampaignsStore()
	const campaignKey = campaigns.getPrimaryCampaignKey
	const promocode = computed(() => campaigns.campaigns[campaignKey]?.promo)
	return promocode
}

export const getBannerComponent = (campaignKey) => {
	const banners = {
		new_subscriber_bonus_2026: SeptemberNewBonus2026Banner,
		spring_sale_2026: SpringSale2026Banner,
	}

	if (banners[campaignKey]) return banners[campaignKey]

	return useIsGroupSubscriptionEnabled() ? GroupSubsBanner : null
}

export const getTermsComponent = (campaignKey) => {
	if (!campaignKey) return null

	switch (campaignKey) {
		case 'new_subscriber_bonus_2026':
			return SeptemberNewBonus2026Terms
		case 'spring_sale_2026':
			return SpringSale2026Terms
		default:
			return null
	}
}
